/**
 * EscapeRequestModal — `/escape` Concierge® request form.
 *
 * Founder ruling 18 Feb 2026 PM-6 — the drawer "Request via
 * Concierge®" CTA must open the SAME shape of form /enjoy uses.
 * Doctrine §8: signature requests are action-required, NOT FYI.
 * The ticket carries `surface=escape` and either `rail=viator` or
 * `rail=signature` so the desk's saved view can separate the
 * inflow.
 *
 * POST /api/escape/request → Zoho ticket + lc_click_intents row.
 */
import React, { useState } from "react";
import { useEmbedMember } from "@/hooks/useEmbedMember";
import { BACKEND_URL } from "@/lib/backend-url";

const T = {
    ink: "#0a0a0c", ink2: "#3a3a42", ink3: "#6a6a72",
    line: "#e7e3dc", line2: "#d2cdc4",
    card: "#fdfaf3", bg: "#f7f3ea", brass: "#8a6e2f",
    err: "#b04525",
};

export default function EscapeRequestModal({ open, context, onClose, onSubmitted }) {
    // context: { rail, product_code, signature_id, title, destination, country }
    const embedMember = useEmbedMember();
    const [memberName, setMemberName] = useState(embedMember?.name  || "");
    const [memberEmail, setMemberEmail] = useState(embedMember?.email || "");
    const [memberPhone, setMemberPhone] = useState(embedMember?.phone || "");
    const [partySize, setPartySize] = useState(2);
    const [preferredSeason, setPreferredSeason] = useState("");
    const [occasion, setOccasion] = useState("");
    const [preferences, setPreferences] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [submitted, setSubmitted] = useState(null);

    if (!open) return null;
    const rail = context?.rail || "viator";
    const isSignature = rail === "signature" || !!context?.signature_id;

    async function submit(e) {
        e.preventDefault();
        if (submitting) return;
        setError(null);
        if (!memberName.trim() || (!memberEmail.trim() && !memberPhone.trim())) {
            setError("Your name and either an email or phone — so the Concierge® can come back to you.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                rail,
                product_code: context?.product_code || null,
                signature_id: context?.signature_id || null,
                title: context?.title || "(unspecified)",
                destination: context?.destination || null,
                country: context?.country || null,
                member_name: memberName.trim(),
                member_email: memberEmail.trim() || null,
                member_phone: memberPhone.trim() || null,
                party_size: partySize,
                preferred_season: preferredSeason.trim() || null,
                occasion: occasion.trim() || null,
                preferences: preferences.trim() || null,
            };
            const r = await fetch(`${BACKEND_URL}/api/escape/request`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!r.ok) {
                const txt = await r.text();
                throw new Error(txt || "Could not send. Please try again.");
            }
            const data = await r.json();
            setSubmitted(data);
            onSubmitted?.(data);
        } catch (e2) {
            setError(e2.message || "Could not send. Please try again.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            data-testid="escape-request-overlay"
            role="dialog"
            aria-label="Request via Concierge"
            onClick={onClose}
            style={{
                position: "fixed", inset: 0, zIndex: 70,
                background: "rgba(10,10,12,0.6)",
                display: "flex", justifyContent: "center", alignItems: "flex-start",
                padding: "60px 22px", overflowY: "auto",
            }}
        >
            <div
                data-testid="escape-request-modal"
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: "min(560px, 100%)",
                    background: T.card, borderRadius: 18,
                    border: `1px solid ${T.line2}`,
                    overflow: "hidden",
                }}
            >
                <div style={{ padding: "26px 28px 16px" }}>
                    <div style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: T.brass, fontFamily: "ui-monospace, monospace" }}>
                        {isSignature ? "Concierge® Signature Escape" : "Escape · curated by Concierge®"}
                    </div>
                    <h3 className="t-serif" style={{ fontSize: 22, margin: "6px 0 0", color: T.ink }}>
                        Request via Concierge®
                    </h3>
                    {context?.title && (
                        <p style={{ fontSize: 13, color: T.ink3, margin: "8px 0 0" }}>
                            {context.title}{context.destination ? ` · ${context.destination}` : ""}
                        </p>
                    )}
                </div>

                {submitted ? (
                    <div style={{ padding: "0 28px 28px" }} data-testid="escape-request-submitted">
                        <div style={{ background: T.bg, border: `1px solid ${T.line}`, borderRadius: 14, padding: "20px 22px" }}>
                            <div style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: T.brass, marginBottom: 8 }}>
                                The Concierge® has it
                            </div>
                            <div className="t-serif" style={{ fontSize: 17, color: T.ink, marginBottom: 6 }}>
                                Reference {submitted.display_ref}
                            </div>
                            <p style={{ fontSize: 13, color: T.ink2, lineHeight: 1.5 }}>
                                A Concierge® will come back to you on the channel you gave us.
                                {isSignature ? " For Signature Escapes the Concierge® personally confirms supplier, route and price." : ""}
                            </p>
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
                            <button
                                data-testid="escape-request-done"
                                onClick={onClose}
                                style={{
                                    background: T.ink, color: T.card, border: 0, borderRadius: 14,
                                    padding: "12px 22px", fontSize: 14, fontWeight: 500, cursor: "pointer",
                                }}
                            >Done</button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit}>
                        <div style={{ padding: "0 28px 8px", display: "grid", gap: 14 }}>
                            {error && (
                                <div
                                    data-testid="escape-request-error"
                                    style={{ background: "#fff0eb", color: T.err, border: `1px solid #f0c4b5`, borderRadius: 12, padding: "10px 12px", fontSize: 13 }}
                                >{error}</div>
                            )}
                            <Field label="Your name">
                                <input
                                    type="text" value={memberName}
                                    onChange={(e) => setMemberName(e.target.value)}
                                    placeholder="First and last"
                                    data-testid="escape-request-name"
                                    autoComplete="name"
                                />
                            </Field>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                                <Field label="Email">
                                    <input
                                        type="email" value={memberEmail}
                                        onChange={(e) => setMemberEmail(e.target.value)}
                                        placeholder="you@domain.com"
                                        data-testid="escape-request-email"
                                        autoComplete="email"
                                    />
                                </Field>
                                <Field label="Phone / WhatsApp">
                                    <input
                                        type="tel" value={memberPhone}
                                        onChange={(e) => setMemberPhone(e.target.value)}
                                        placeholder="+91 9876 …"
                                        data-testid="escape-request-phone"
                                        autoComplete="tel"
                                    />
                                </Field>
                            </div>
                            <Field label="Party size">
                                <input
                                    type="number" min="1" max="50" value={partySize}
                                    onChange={(e) => setPartySize(parseInt(e.target.value || "1", 10))}
                                    data-testid="escape-request-party-size"
                                />
                            </Field>
                            <Field label="Preferred season / dates">
                                <input
                                    type="text" value={preferredSeason}
                                    onChange={(e) => setPreferredSeason(e.target.value)}
                                    placeholder="e.g. December · Christmas week"
                                    data-testid="escape-request-preferred-season"
                                />
                            </Field>
                            <Field label="Occasion (optional)">
                                <input
                                    type="text" value={occasion}
                                    onChange={(e) => setOccasion(e.target.value)}
                                    placeholder="anniversary, birthday, family trip…"
                                    data-testid="escape-request-occasion"
                                />
                            </Field>
                            <Field label="Anything else?">
                                <textarea
                                    rows={3} value={preferences}
                                    onChange={(e) => setPreferences(e.target.value)}
                                    placeholder="Date flexibility, special access, dietary needs, mobility…"
                                    data-testid="escape-request-preferences"
                                />
                            </Field>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "18px 28px 24px", borderTop: `1px solid ${T.line}`, marginTop: 8 }}>
                            <button
                                type="button"
                                onClick={onClose}
                                data-testid="escape-request-cancel"
                                style={{
                                    background: "transparent", color: T.ink2,
                                    border: `1px solid ${T.line2}`, borderRadius: 14,
                                    padding: "12px 18px", fontSize: 14, cursor: "pointer",
                                }}
                            >Cancel</button>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="escape-request-submit"
                                style={{
                                    background: T.ink, color: T.card, border: 0,
                                    borderRadius: 14, padding: "12px 22px",
                                    fontSize: 14, fontWeight: 500,
                                    cursor: submitting ? "not-allowed" : "pointer",
                                    opacity: submitting ? 0.6 : 1,
                                }}
                            >
                                {submitting ? "Sending…" : "Send to Concierge®"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: T.ink3, fontFamily: "ui-monospace, monospace" }}>{label}</span>
            <span
                style={{
                    display: "block",
                }}
            >
                {React.Children.map(children, (c) => React.cloneElement(c, {
                    style: {
                        width: "100%", boxSizing: "border-box",
                        padding: "10px 12px", border: `1px solid ${T.line2}`,
                        borderRadius: 10, fontSize: 14, color: T.ink, background: "#fff",
                        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                        outline: "none",
                    },
                }))}
            </span>
        </label>
    );
}
